import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listVideos, toggleWatchLater, resolveFileUrl } from '../lib/api';
import { useApp } from '../context/AppContext';
import CategoryRow from '../components/CategoryRow';
import FreeTierPanel from '../components/FreeTierPanel';
import { Play, Plus, Info, Loader2, Music2, Headphones, GraduationCap, ArrowRight, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

/** Decluttered Home — music-first hero, 3 quick-pick rails, then 1-2 video rows.
 * Removed: featured video hero, shorts strip, ad banner, every category row.
 * The discovery surface is now Reel + Artists + Academy + Hub — surfaced via FreeTierPanel + 4 cards.
 */
export default function Home() {
  const { user, categories } = useApp();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setVideos(await listVideos({ limit: 60 })); }
      catch { toast.error('Failed to load videos'); }
      finally { setLoading(false); }
    })();
  }, []);

  // Only ONE category row, only if substantial — keeps Home airy
  const trendingRow = useMemo(() => {
    const list = [...videos].filter(v => v.type !== 'short').sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 8);
    return list.length >= 4 ? list : null;
  }, [videos]);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="home-page">
      <MusicFirstHero name={user?.name?.split(' ')[0]} />

      <FreeTierPanel />

      {trendingRow && (
        <CategoryRow title="Trending video this week" videos={trendingRow} moreHref="/category/trending" category="trending" />
      )}
    </div>
  );
}

function MusicFirstHero({ name }) {
  return (
    <header className="mb-8 md:mb-10" data-testid="home-hero">
      <div className="chip mb-3"><Music2 className="w-3 h-3" /> Music-first since 2026</div>
      <h1 className="font-display text-[34px] sm:text-[44px] md:text-[60px] leading-[0.95] max-w-[860px]">
        {name ? <>Welcome back, <span className="text-[#ff2e63]">{name}</span>.</> : <>Listen. Learn. <span className="text-[#ff2e63]">Build a music career.</span></>}
      </h1>
      <p className="text-white/65 text-[14px] md:text-[16px] mt-3 max-w-[640px] leading-relaxed">
        ReaperCast is the operating system for indie music — listen in the Reel, follow your favourites, learn the business in the Academy, get paid through the Hub. Video hosting included free for every account.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        <QuickCard to="/feed"        Icon={Headphones}     color="#22d3ee" title="Music Reel"           desc="Swipe-style indie discovery." />
        <QuickCard to="/academy"     Icon={GraduationCap}  color="#ffcf52" title="Music Biz Academy"    desc="Finish a track → unlock an AI coach." />
        <QuickCard to="/income"      Icon={DollarSign}     color="#34d399" title="Income Discovery"     desc="15+ income streams + AI advisor." />
        <QuickCard to="/artists"     Icon={Music2}         color="#a78bfa" title="Artists"              desc="Follow indie artists. Build your reel." />
      </div>
    </header>
  );
}

function QuickCard({ to, Icon, color, title, desc }) {
  return (
    <Link to={to} data-testid={`home-quick-${to.slice(1)}`} className="group card-shell p-4 hover-lift transition-all">
      <div className="flex items-center justify-between mb-2">
        <span className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: color + '22', color }}><Icon className="w-4 h-4" /></span>
        <ArrowRight className="w-4 h-4 text-white/35 transition-transform group-hover:translate-x-1" style={{ color }} />
      </div>
      <div className="font-display text-[16px]">{title}</div>
      <p className="text-[12px] text-white/60 mt-1 leading-relaxed">{desc}</p>
    </Link>
  );
}
